import { createSlice } from '@reduxjs/toolkit';

import _flatMap from 'lodash/flatMap';
import _groupBy from 'lodash/groupBy';
import _mapValues from 'lodash/mapValues';
import _sortBy from 'lodash/sortBy';
import _toPairs from 'lodash/toPairs';

import historyDataRaw from 'data/history.json';
import songsDataRaw from 'data/songs.json';
import albumDataRaw from 'data/albums.json';
import dvdsDataRaw from 'data/dvds.json';
import peopleDataRaw from 'data/people.json';
import toursDataRaw from 'data/tours.json';
import concertsDataRaw from 'data/concerts.json';
import awardsDataRaw from 'data/awards.json';

import {
  HistoryState,
  Song,
  PeopleDetails,
  Album,
  Tour,
  Concert,
  YearsHistory,
  History,
  RecurringEvents,
  Dvd,
  Award
} from 'interfaces';

const initialStateDefault: HistoryState = {
  history: null
};

const historyData: History[] = historyDataRaw as unknown as History[];
const peopleData: PeopleDetails[] = peopleDataRaw as unknown as PeopleDetails[];
const albumsData: Album[] = albumDataRaw as unknown as Album[];
const dvdsData: Album[] = dvdsDataRaw as unknown as Dvd[];
const songsData: Song[] = songsDataRaw as unknown as Song[];
const toursData: Tour[] = toursDataRaw as unknown as Tour[];
const concertsData: Concert[] = concertsDataRaw as unknown as Concert[];
const awardsData: Award[] = awardsDataRaw as unknown as Award[];

const historySlice = createSlice({
  name: 'history',
  initialState: initialStateDefault,
  reducers: {
    getAllHistoryData (state) {
      const historyYears = historyData
        .filter((el) => el.id !== 'toru_joins' && el.id !== 'ryota_joins')
        .reduce((acc: YearsHistory, current: History) => {
        const currentYear = current.date.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], current ] : [ current ] };
      }, {});

      const singleYears = songsData.filter((el) => el.single).reduce((acc: YearsHistory, current: Song) => {
        const currentYear = current.singleRelease.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        const element = { id: current?.id, type: 'single', date: current?.singleRelease };
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], element ] : [ element ] };
      }, {});

      const albumsYears = albumsData.filter((el) => el.typeId !== 'single').reduce((acc: YearsHistory, current: Album) => {
        const currentYear = current.releaseDate.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        const element = { id: current?.id, type: current.typeId, date: current?.releaseDate, isInternational: current?.international };
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], element ] : [ element ] };
      }, {});

      const toursYears = toursData.reduce((acc: YearsHistory, current: Tour) => {
        const currentYear = current.dateStart.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        const element = {
          id: current?.id,
          type: 'tour',
          date: current?.dateStart,
          endDate: current?.dateEnd,
          hasHistoryDescription: current?.hasHistoryDescription
        };
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], element ] : [ element ] };
      }, {});

      const concertsYears = concertsData.reduce((acc: YearsHistory, current: Concert) => {
        const currentYear = current.date.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        const element = {
          id: current?.id,
          type: 'concert',
          date: current?.date,
          hasHistoryDescription: current?.hasHistoryDescription
        };
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], element ] : [ element ] };
      }, {});

      const dvdsYears = dvdsData.reduce((acc: YearsHistory, current: Dvd) => {
        const currentYear = current.releaseDate.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        const element = { id: current?.id, type: 'dvd', date: current?.releaseDate };
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], element ] : [ element ] };
      }, {});

      const awardsYears = awardsData.reduce((acc: YearsHistory, current: Award) => {
        const currentYear = current.date.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        const element = {
          id: current?.id,
          type: 'award',
          date: current?.date,
          isDateWithoutDay: current.isDateWithoutDay,
          hasHistoryDescription: current?.hasHistoryDescription
        };
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], element ] : [ element ] };
      }, {});

      const recurringEvents = peopleData.filter((el) => el.isMember).reduce((acc: RecurringEvents[], current: PeopleDetails) => {
        const currentYear = current.birthdate.split('-');
        acc.push({ id: current.id, dateTemplate: `-${currentYear[1]}-${currentYear[2]}`, birthdayYear: currentYear[0] })
        return acc;
      }, []);

      const mergeObjects = [historyYears, singleYears, albumsYears, dvdsYears, toursYears, concertsYears, awardsYears];

      const allPairs = _flatMap(mergeObjects, (obj: YearsHistory) => _toPairs(obj));
      const grouped = _groupBy(allPairs, pair => pair[0]);

      const joinDates = historyData
        .filter((el) => el.isJoined)
        .reduce((acc: Record<string, Date>, { id, date }) => {
          acc[id] = new Date(date);
          return acc;
        }, {});

      const mergedHistory = _mapValues(grouped, (pairs, year) => {
        const items = _flatMap(pairs, pair => pair[1]);

        const recEventsForYear: History[] = recurringEvents
          .filter(event => {
            const joinDate = joinDates[`${event.id}_joins`];
            return joinDate <= new Date(`${year}${event.dateTemplate}`) && new Date(`${year}${event.dateTemplate}`) <= new Date();
          })
          .map((event: RecurringEvents) => ({
            ...event,
            id: event.id,
            type: 'memberBirthday',
            date: `${year}${event.dateTemplate}`
          }));

        return _sortBy([...items, ...recEventsForYear], 'date');
      });

      return {
        ...initialStateDefault,
        history: mergedHistory
      };
    }
  }
});

export const { getAllHistoryData } = historySlice.actions;

export default historySlice.reducer;
